import React, { useState, useEffect } from "react";
import {
  Input,
  Form,
  Col,
  Row,
  Button,
  Select,
  Modal,
  Divider,
  Card,
  DatePicker,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Config from "../../util/ApiforContext";
import RequestHandler from "util/RequestHandler";
import { defaultValidateMessages } from "./Helper";
import moment from "moment";

const formItemLayout = {
  labelCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 8,
    },
  },
  wrapperCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 16,
    },
  },
};

const defaultPermissionState = {
  accountPermissions: ["01", "02", "03", "04", "05"],
  cardPermissions: ["07", "08", "09"],
};

const permissionNames = {
  "01": "Temel Hesap Bilgisi",
  "02": "Ayrıntılı Hesap Bilgisi",
  "03": "Bakiye Bilgisi",
  "04": "Temel Hesap Hareketleri Bilgisi",
  "05": "Ayrıntılı Hesap Hareketleri Bilgisi",
  "07": "Temel Kart Bilgisi",
  "08": "Detaylı Kart Bilgisi",
  "09": "Ayrıntılı Kart İşlem Bilgisi",
};

const accountPermissionOptions = ["01", "02", "03", "04", "05"];
const cardPermissionOptions = ["07", "08", "09"];

const BkmBankInfoAddForm = (props) => {
  const [loading, setLoading] = useState(false);
  const [bankSelect, setBankSelect] = useState({
    data: [],
    loading: false,
  });
  const [permissionState, setPermissionState] = useState(
    defaultPermissionState,
  );
  const [redirectModal, setRedirectModal] = useState({
    visible: false,
    redirectUrl: null,
    consentId: null,
    cancelLoading: false,
  });

  const [form] = Form.useForm();

  const today = moment().format("DD-MM-YYYY HH:mm:ss");
  const sixMonthsFromNow = moment()
    .add(6, "month")
    .format("DD-MM-YYYY  HH:mm:ss");
  const [startDate, setStartDate] = useState(today);
  const [startDateForDto, setStartDateForDto] = useState("");

  useEffect(() => {
    getNotAddedBanks();
  }, []);

  useEffect(() => {
    if (props.data) {
      form.setFieldsValue(props.data);
    }
  }, [props.data]);

  const getNotAddedBanks = () => {
    setBankSelect({
      ...bankSelect,
      loading: true,
    });
    RequestHandler.get(`/BkmBankInfo/NotAddedBanks?$select=id,name`).then(
      (response) => {
        setBankSelect({
          data: response,
          loading: false,
        });
      },
    );
  };

  const onFinish = (values) => {
    const permissions = getPermissions(permissionState);

    if (!permissions.includes("01") && !permissions.includes("07")) {
      setLoading(false);
      Modal.warning({
        title: "İzin Seçimi Gerekli",
        content: "En az bir hesap veya kart izni seçmelisiniz.",
      });
      return;
    }

    RequestHandler.post(`/BkmBankInfo`, { ...values, permissions }, false).then(
      (response) => {
        setLoading(false);
        if (response.success) {
          getNotAddedBanks();
          form.resetFields();
          setPermissionState(defaultPermissionState);
          if (props.onSuccess) props.onSuccess();

          setRedirectModal({ ...response.data, visible: true });
        }
      },
    );
  };

  const onFinishFailed = () => setLoading(false);

  const getIdentityType = (type) => {
    const identityTypes = {
      V: "Vergi No",
      K: "T.C. Kimlik No",
      M: "Müşteri No",
      Y: "Yabancı Kimlik No",
      P: "Pasaport No",
    };

    return identityTypes[type];
  };

  const handleStartDateChange = (date, dateString) => {
    setStartDate(dateString);
    if (date) {
      const currentDateTime = moment();
      const fullStartDate = date
        .hour(currentDateTime.hour())
        .minute(currentDateTime.minute())
        .second(currentDateTime.second())
        .format();
      setStartDateForDto(fullStartDate);
    }
  };
  const disabledDate = (current) => {
    return (
      current &&
      (current < moment().startOf("day").add(1, "day") ||
        current > moment().add(6, "months"))
    );
  };

  const getPermissions = (state) => {
    return [
      ...normalizeAccountPermissions(state.accountPermissions, false),
      ...normalizeCardPermissions(state.cardPermissions),
    ];
  };

  const normalizeAccountPermissions = (
    permissions,
    addMainPermission = true,
  ) => {
    const permissionSet = new Set(permissions || []);

    if (!permissionSet.has("01") && !addMainPermission) {
      return [];
    }

    if (permissionSet.size > 0) permissionSet.add("01");
    if (permissionSet.has("02")) permissionSet.add("01");
    if (permissionSet.has("03")) permissionSet.add("01");
    if (permissionSet.has("04")) permissionSet.add("01");
    if (permissionSet.has("05")) {
      permissionSet.add("01");
      permissionSet.add("04");
    }

    if (!permissionSet.has("04")) permissionSet.delete("05");

    return accountPermissionOptions.filter((permission) =>
      permissionSet.has(permission),
    );
  };

  const normalizeCardPermissions = (permissions) => {
    const permissionSet = new Set(permissions || []);

    if (!permissionSet.has("07")) {
      return [];
    }

    return cardPermissionOptions.filter((permission) =>
      permissionSet.has(permission),
    );
  };

  const handleAccountPermissionsChange = (permissions) => {
    setPermissionState((prevState) => {
      if (
        prevState.accountPermissions.includes("01") &&
        !permissions.includes("01")
      ) {
        return {
          ...prevState,
          accountPermissions: [],
        };
      }

      const nextPermissions =
        prevState.accountPermissions.includes("04") &&
        !permissions.includes("04")
          ? permissions.filter((permission) => permission !== "05")
          : permissions;

      return {
        ...prevState,
        accountPermissions: normalizeAccountPermissions(nextPermissions),
      };
    });
  };

  const handleCardPermissionsChange = (permissions) => {
    setPermissionState((prevState) => ({
      ...prevState,
      cardPermissions: normalizeCardPermissions(permissions),
    }));
  };

  const width = window.innerWidth;
  const breakpoint = 480;
  const textRightClass = width < breakpoint ? "" : "gx-text-right";
  return (
    <>
      <Form
        {...formItemLayout}
        validateMessages={defaultValidateMessages}
        form={form}
        name="bkmBankInfoAdd"
        initialValues={{
          remember: true,
        }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Row>
          <Col span={24}>
            <Form.Item
              hidden
              name="returnUrl"
              initialValue={Config.frontEndUrl + "transaction-bank-info"}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="bankId"
              label="Hesap Hizmet Sağlayıcısı"
              rules={[{ required: true }]}
            >
              <Select
                allowClear
                showSearch
                placeholder="Seçiniz"
                filterOption={(input, option) =>
                  option.children
                    .toLocaleLowerCase("tr-TR")
                    .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
                }
                loading={bankSelect.loading}
              >
                {bankSelect.data.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="customerType"
              label="Müşteri Türü"
              rules={[{ required: true }]}
            >
              <Select placeholder="Seçiniz">
                <Select.Option key="B" value="B">
                  Bireysel
                </Select.Option>
                <Select.Option key="K" value="K">
                  Kurumsal
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Son Erişim Tarihi"
              name="accessValidityDate"
              initialValue={moment(sixMonthsFromNow, "DD-MM-YYYY HH:mm:ss")}
              rules={[
                {
                  required: true,
                  message: "Son Erişim Tarihi Alanı Zorunlu Alandır",
                },
              ]}
            >
              <DatePicker
                onChange={handleStartDateChange}
                format="DD-MM-YYYY"
                disabledDate={disabledDate}
              />
            </Form.Item>

            <Divider>Hesap İzinleri</Divider>

            <Form.Item label="Hesap Yetkileri">
              <Select
                mode="multiple"
                placeholder="Seçiniz"
                value={permissionState.accountPermissions}
                onChange={handleAccountPermissionsChange}
              >
                {accountPermissionOptions.map((permission) => (
                  <Select.Option key={permission} value={permission}>
                    {permissionNames[permission]}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Divider>Kart İzinleri</Divider>

            <Form.Item label="Kart Yetkileri">
              <Select
                mode="multiple"
                placeholder="Seçiniz"
                value={permissionState.cardPermissions}
                onChange={handleCardPermissionsChange}
              >
                {cardPermissionOptions.map((permission) => (
                  <Select.Option
                    key={permission}
                    value={permission}
                    disabled={
                      permission !== "07" &&
                      !permissionState.cardPermissions.includes("07")
                    }
                  >
                    {permissionNames[permission]}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Col style={{ textAlign: "right" }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                htmlType="submit"
                onClick={() => setLoading(true)}
                loading={loading}
              >
                Rıza Oluştur
              </Button>
            </Col>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card className="gx-card">
              <p>
                <strong>Temel Hesap Bilgisi, </strong>Hesap Kısa Adı, Para
                Birimi, Hesap Numarası (IBAN), Hesap Türü (bireysel, ticari),
                Hesap Durumu, Hesap Referansı (Hesap numarası için oluşturulan
                ID), Hesap Sahibi Ünvanı, Hesap Tipi, Hesap Ürün Adı, Şube Adı
                bilgilerini içerir.
              </p>
              <p>
                <strong>Ayrıntılı Hesap Bilgisi, </strong>Temel Hesap Bilgisine
                ek olarak Hesap Açılış Tarihi bilgisini içerir.
              </p>
              <p>
                <strong>Bakiye Bilgisi Göster, </strong>hesaplarınızın bakiye
                bilgilerini içerir.
              </p>
              <p>
                <strong>Temel Hesap Hareketleri Bilgisi, </strong>Hesap
                Numarası, İşlem Numarası, İşlem Referans Numarası, İşlem Tutarı,
                Para Birimi, İşlem Gerçekleşme Zamanı, İşlem Kanalı, Borç/Alacak
                Göstergesi, İşlem Türü, Ödeme Sistemi No bilgilerini içerir.
              </p>
              <p>
                <strong>Ayrıntılı Hesap Hareketleri Bilgisi, </strong>Temel
                Hesap Hareketleri Bilgisine ek olarak İşlem Açıklaması, Karşı
                Taraf Maskeli Ünvan, Karşı Taraf Maskeli IBAN bilgilerini
                içerir.
              </p>
              <p>
                <strong>Temel Kart Bilgisi, </strong>Kart Numarası, Asıl Kart
                Numarası, Kart Tipi, Alt Kart Tipi, Kart Formu, Kart Türü, Kart
                Statüsü, Kart Sahibi, Ekstre Türleri, Kart Rumuzu ve Kart Şeması
                bilgilerine erişilebilir.
              </p>
              <p>
                <strong>Detaylı Kart Bilgisi, </strong>Ekstre Türü, Toplam Kart
                Limiti, Kullanılabilir Kart Limiti, Dönem İçi Hareket Toplamı,
                Ekstre Borcu, Kalan Ekstre Borcu, Asgari Ödeme Tutarı, Hesap
                Kesim Tarihi, Puan Bilgisi, Nakit Çekim Limiti, Kalan Toplam
                Taksit Tutarı ve Dönem Taksit Tutar Bilgisi gibi bilgilere
                erişilebilir.
              </p>
              <p>
                <strong>Ayrıntılı Kart İşlem Bilgisi, </strong>Kart
                hareketlerine erişilebilir.
              </p>
            </Card>
          </Col>
        </Row>
      </Form>

      <Modal
        id="redirectModal"
        title={`${redirectModal.bankName} Rıza Bilgisi`}
        visible={redirectModal.visible}
        onOk={() => {
          setRedirectModal({ visible: false });
          window.location = redirectModal.redirectUrl;
        }}
        okText="Onaylıyorum"
        onCancel={() => {
          setRedirectModal({
            ...redirectModal,
            visible: false,
            cancelLoading: false,
          });
        }}
        width={1000}
        cancelButtonProps={{ loading: redirectModal.cancelLoading }}
        cancelText="İptal Et"
        closable={false}
      >
        <Divider>İzin Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Rıza No :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {redirectModal.consentId}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Rıza Geçerlilik Tarihi
          </Col>
          <Col xs={24} lg={16}>
            {moment(redirectModal.accessValidityDate).format("DD.MM.YYYY")}
          </Col>
        </Row>

        {(redirectModal.permissions?.includes("04") ||
          redirectModal.permissions?.includes("05")) && (
          <>
            <Row>
              <Col
                xs={24}
                lg={8}
                className={`${textRightClass} gx-font-weight-semi-bold`}
              >
                İşlem Sorgu Başlangıç Tarihi
              </Col>
              <Col xs={24} lg={16}>
                {moment(redirectModal.queryStartDate).format("DD.MM.YYYY")}
              </Col>
            </Row>
            <Row>
              <Col
                xs={24}
                lg={8}
                className={`${textRightClass} gx-font-weight-semi-bold`}
              >
                İşlem Sorgu Bitiş Tarihi
              </Col>
              <Col xs={24} lg={16}>
                {moment(redirectModal.queryEndDate).format("DD.MM.YYYY")}
              </Col>
            </Row>
          </>
        )}

        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            İzin Türü
          </Col>
          <Col xs={24} lg={16}>
            <ul>
              {redirectModal.permissions?.map((x) => (
                <li key={x}>{permissionNames[x] || x}</li>
              ))}
            </ul>
          </Col>
        </Row>

        <Divider>Kimlik Bilgileri</Divider>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Müşteri Türü :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {redirectModal.customerType === "K" ? "Kurumsal" : "Bireysel"}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Kimlik Türü :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {getIdentityType(redirectModal.identityType)}
          </Col>
        </Row>
        <Row>
          <Col
            xs={24}
            lg={8}
            className={`${textRightClass} gx-font-weight-semi-bold`}
          >
            Kimlik No :{" "}
          </Col>
          <Col xs={24} lg={16}>
            {redirectModal.identityValue}
          </Col>
        </Row>

        {redirectModal.customerType === "K" && (
          <>
            <Row>
              <Col
                xs={24}
                lg={8}
                className={`${textRightClass} gx-font-weight-semi-bold`}
              >
                Kurum Kimlik Türü :{" "}
              </Col>
              <Col xs={24} lg={16}>
                {getIdentityType(redirectModal.corporateIdentityType)}
              </Col>
            </Row>
            <Row>
              <Col
                xs={24}
                lg={8}
                className={`${textRightClass} gx-font-weight-semi-bold`}
              >
                Kurum Kimlik No :{" "}
              </Col>
              <Col xs={24} lg={16}>
                {redirectModal.corporateIdentityValue}
              </Col>
            </Row>
          </>
        )}

        <Divider />

        <Row>
          <Col span={24}>
            <p>
              Yukarıdaki bilgiler ile hesap bilgileri rızasını tamamlamak için{" "}
              <strong>{redirectModal.bankName}</strong> tarafına
              yönlendirileceksiniz.
            </p>
            <p>İşlemi onaylıyor musunuz?</p>
          </Col>
        </Row>
      </Modal>
    </>
  );
};
export default BkmBankInfoAddForm;
